import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import BaseRoute from './messaging-routes/base-route.gjs';
import Component from '@glimmer/component';
import FlexColumn from 'ember-simplepractice/components/spds/layout/flex/column';
import NegativeButton from 'ember-simplepractice/components/spds/action/button/negative';
import SecondaryButton from 'ember-simplepractice/components/spds/action/button/secondary';
import styles from './confirmation-modal.module.scss';

/**
 * Full-screen confirmation modal. Invoke as an overlay/replacement from any
 * messaging screen — not a navigable route.
 *
 * @param {string} @title - Primary question / heading
 * @param {string} @body - Supporting description
 * @param {string} @confirmLabel - Primary (destructive) button label
 * @param {string} [@cancelLabel] - Cancel button label (defaults to emc.cancel)
 * @param {Function} @onConfirm - Async action run when confirm is clicked
 * @param {Function} @onCancel - Action run when cancel is clicked
 */
export default class ConfirmationModal extends Component {
  @service intl;

  get cancelLabel() {
    return this.args.cancelLabel ?? this.intl.t('emc.cancel');
  }

  get isConfirmRunning() {
    return this.confirmTask.isRunning;
  }

  cancel = () => {
    if (this.isConfirmRunning) {
      return;
    }
    this.args.onCancel?.();
  };

  confirm = () => {
    this.confirmTask.perform();
  };

  confirmTask = task({ drop: true }, async () => {
    try {
      await this.args.onConfirm?.();
    } catch {
      // Caller handles errors (e.g. toast). Stay mounted so the user can retry or cancel.
    }
  });

  <template>
    <BaseRoute class="{{styles.component}} confirmation-modal" data-test-confirmation-modal>
      <:header>
        <span class="title" data-test-confirmation-modal-title>{{t "emc.confirm"}}</span>
      </:header>
      <:default>
        <FlexColumn @gap="40px" class="content">
          <FlexColumn @gap="4px" data-test-confirmation-modal-body>
            <h2 class="confirm-question" data-test-confirmation-modal-question>
              {{@title}}
            </h2>
            <p class="confirm-description" data-test-confirmation-modal-description>
              {{@body}}
            </p>
          </FlexColumn>

          <FlexColumn @gap="8px" data-test-confirmation-modal-actions>
            <SecondaryButton
              @disabled={{this.isConfirmRunning}}
              {{on "click" this.cancel}}
              class="confirm-action-button"
              data-test-confirmation-modal-cancel-button
            >
              {{this.cancelLabel}}
            </SecondaryButton>

            <NegativeButton
              @disabled={{this.isConfirmRunning}}
              {{on "click" this.confirm}}
              class="confirm-action-button"
              data-test-confirmation-modal-confirm-button
            >
              {{@confirmLabel}}
            </NegativeButton>
          </FlexColumn>
        </FlexColumn>
      </:default>
    </BaseRoute>
  </template>
}
